import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { PlusCircle } from "lucide-react";
import { MobileNavigation } from "@/components/MobileNavigation";
import { PremiumRoute } from '@/components/PremiumRoute';
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { OpportunityForm } from "@/components/opportunities/OpportunityForm";
import { OpportunityList } from "@/components/opportunities/OpportunityList";
import { Badge } from "@/components/ui/badge";
import { Database } from "@/integrations/supabase/types";
import { SparklesCore } from "@/components/ui/sparkles";

type OpportunityType = Database['public']['Enums']['opportunity_type'];
type FilterType = OpportunityType | 'all';

const opportunityTypes: { label: string; value: FilterType }[] = [
  { label: 'All', value: 'all' },
  { label: 'Audition', value: 'audition' },
  { label: 'Workshop', value: 'workshop' },
  { label: 'Residency', value: 'residency' },
  { label: 'Job', value: 'job' }
];

const Opportunities = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<FilterType>('all');

  const handleScrapeOpportunities = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('scrape-opportunities');
      
      if (error) {
        console.error('Error scraping opportunities:', error);
        toast({
          title: "Error",
          description: "Failed to scrape opportunities. Please try again later.",
          variant: "destructive",
        });
        return;
      }

      console.log('Scrape response:', data);
      toast({
        title: "Success",
        description: data.message || "Successfully scraped opportunities",
      });
    } catch (error) {
      console.error('Error invoking scrape function:', error);
      toast({
        title: "Error",
        description: "Failed to connect to scraping service",
        variant: "destructive",
      });
    }
  };

  return (
    <PremiumRoute pageName="Opportunities">
      <div className="relative min-h-screen bg-gradient-to-b from-background via-background/80 to-background/40 overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <SparklesCore
            background="transparent"
            minSize={0.6}
            maxSize={1.4}
            particleColor="#FF6B6B"
            className="w-full h-full"
            particleDensity={30}
          />
        </div>

        <div className="container mx-auto py-4 md:py-8 pb-20 md:pb-8 md:mt-16 relative z-10">
          <div className="flex flex-col space-y-6">
            <div className="flex justify-between items-center">
              <div className="relative w-8 h-8">
                <SparklesCore
                  background="transparent"
                  minSize={0.4}
                  maxSize={1}
                  particleColor="#FFE66D"
                  className="w-full h-full"
                  particleDensity={20}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleScrapeOpportunities}>
                  {t('opportunities.scrape')}
                </Button>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <PlusCircle className="w-4 h-4 mr-2" /> 
                      {t('opportunities.post')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('opportunities.form.title')}</DialogTitle>
                    </DialogHeader>
                    <OpportunityForm />
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4 relative">
              <div className="absolute inset-0 -z-10">
                <SparklesCore
                  background="transparent"
                  minSize={0.2}
                  maxSize={0.8}
                  particleColor="#7FC8A9"
                  className="w-full h-full"
                  particleDensity={15}
                />
              </div>
              {opportunityTypes.map((type) => (
                <Badge
                  key={type.value}
                  variant={selectedType === type.value ? "default" : "outline"}
                  className="cursor-pointer hover:bg-primary/80 relative z-10"
                  onClick={() => setSelectedType(type.value)}
                >
                  {type.label}
                </Badge>
              ))}
            </div>
          </div>
          <OpportunityList selectedType={selectedType} />
        </div>
      </div>
      <MobileNavigation />
    </PremiumRoute>
  );
};

export default Opportunities;